import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import ZohoDataMapping from "./ZohoDataMapping";
import SkillControlsPanel from "../SkillControlsPanel";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  Divider,
  FieldInput
} from "@/components/ds";
import IconByName from "@/components/ds/IconByName";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import { ensureDefaultRoutingWorkflow } from "@/pages/Messaging/Workflows/defaultWorkflowHelper";
const ZH_COLOR = "#E42527";

// ─── Exported nav helper ───────────────────────────────────────────────────────

export function BackToIntegrationLandingZoho() {
  const navigate = useNavigate();
  return (
    <GhostButton
      onClick={() => navigate("/integrations")}
      className="!text-zinc-600 hover:!text-zinc-800 hover:!no-underline !gap-2"
    >
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
          clipRule="evenodd"
        />
      </svg>
      Back to Integrations
    </GhostButton>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ZohoConfig {
  id: number;
  siteId: number;
  displayName: string;
  clientIdMasked: string;
  dataCenter: string;
  enabled: boolean;
  createdAt: string;
  pushTranscripts: boolean;
}

type TabId =
  | "overview"
  | "accounts"
  | "ai-tools"
  | "field-reference"
  | "data-mapping"
  | "logs";

const DATA_CENTERS = [
  { value: "com", label: "United States (.com)" },
  { value: "eu", label: "Europe (.eu)" },
  { value: "in", label: "India (.in)" },
  { value: "au", label: "Australia (.au)" },
  { value: "jp", label: "Japan (.jp)" }
];

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Contact & lead management",
    icon: "User",
    desc: "Look up, create, and update contacts and leads in real time from chat"
  },
  {
    title: "Deal tracking",
    icon: "Briefcase",
    desc: "See all open deals, stages, and values during sales conversations"
  },
  {
    title: "Account intelligence",
    icon: "Building",
    desc: "Company-level view with all associated contacts, deals, and activity history"
  },
  {
    title: "Activity automation",
    icon: "Edit",
    desc: "Auto-log chats as tasks or calls, set follow-up reminders from workflow nodes"
  },
  {
    title: "Pipeline reporting",
    icon: "BarChart2",
    desc: "Conversion rates, pipeline value, and stage distribution during coaching conversations"
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  {
    name: "zoho_get_contact",
    label: "Get contact",
    group: "Contacts",
    description:
      "Retrieve a contact by ID — name, email, phone, account, and recent activity."
  },
  {
    name: "zoho_search_contacts",
    label: "Search contacts",
    group: "Contacts",
    description:
      "Find contacts by email, phone, or name. Returns matching records with deal associations."
  },
  {
    name: "zoho_create_contact",
    label: "Create contact",
    group: "Contacts",
    description: "Add a new contact with name, email, phone, and account link."
  },
  {
    name: "zoho_update_contact",
    label: "Update contact",
    group: "Contacts",
    description:
      "Update contact fields — email, phone, address, or custom fields."
  },
  {
    name: "zoho_get_contact_activities",
    label: "Get contact activities",
    group: "Contacts",
    description:
      "All tasks, calls, and events associated with a contact — status and due dates."
  },

  {
    name: "zoho_get_lead",
    label: "Get lead",
    group: "Leads",
    description:
      "Full lead details: name, source, status, company, and assigned owner."
  },
  {
    name: "zoho_search_leads",
    label: "Search leads",
    group: "Leads",
    description:
      "Find leads by email, name, or company. Returns status and source."
  },
  {
    name: "zoho_create_lead",
    label: "Create lead",
    group: "Leads",
    description:
      "Create a new lead with name, email, company, source, and lead score."
  },
  {
    name: "zoho_convert_lead",
    label: "Convert lead",
    group: "Leads",
    description:
      "Convert a lead to a contact, account, and deal in one operation."
  },

  {
    name: "zoho_get_deal",
    label: "Get deal",
    group: "Deals",
    description:
      "Full deal details: name, stage, amount, close date, and associated contacts."
  },
  {
    name: "zoho_search_deals",
    label: "Search deals",
    group: "Deals",
    description:
      "Find deals by name, contact, or account. Returns stage and amount."
  },
  {
    name: "zoho_create_deal",
    label: "Create deal",
    group: "Deals",
    description:
      "Create a new deal linked to a contact or account with stage and expected revenue."
  },
  {
    name: "zoho_update_deal",
    label: "Update deal",
    group: "Deals",
    description: "Update deal stage, amount, close date, or probability."
  },
  {
    name: "zoho_get_deal_stages",
    label: "Get deal stages",
    group: "Deals",
    description:
      "All pipeline stages available in Zoho CRM with probability percentages."
  },

  {
    name: "zoho_get_account",
    label: "Get account",
    group: "Accounts",
    description:
      "Account details: company name, website, industry, revenue, and linked contacts count."
  },
  {
    name: "zoho_search_accounts",
    label: "Search accounts",
    group: "Accounts",
    description:
      "Find accounts by name or website. Returns industry, contacts, and deal summary."
  },
  {
    name: "zoho_create_account",
    label: "Create account",
    group: "Accounts",
    description:
      "Create a new company account with name, website, industry, and address."
  },

  {
    name: "zoho_create_task",
    label: "Create task",
    group: "Activities",
    description:
      "Create a follow-up task for a contact or deal with due date and priority."
  },
  {
    name: "zoho_create_call",
    label: "Create call log",
    group: "Activities",
    description:
      "Log a call activity against a contact or deal — duration, outcome, and notes."
  },
  {
    name: "zoho_create_event",
    label: "Create event",
    group: "Activities",
    description:
      "Schedule an event (meeting, demo) linked to a contact or deal with date and attendees."
  },
  {
    name: "zoho_get_activities",
    label: "Get activities",
    group: "Activities",
    description:
      "All activities for a contact or deal — tasks, calls, events — with status."
  },

  {
    name: "zoho_create_note",
    label: "Create note",
    group: "Notes",
    description: "Add an internal note to a contact, lead, deal, or account."
  },
  {
    name: "zoho_get_notes",
    label: "Get notes",
    group: "Notes",
    description:
      "Retrieve notes for a record — content, author, and created date."
  },

  {
    name: "zoho_get_pipeline_report",
    label: "Get pipeline report",
    group: "Reports",
    description:
      "Pipeline summary — deal counts, total value, and average deal size by stage."
  },
  {
    name: "zoho_get_conversion_report",
    label: "Get conversion report",
    group: "Reports",
    description:
      "Lead-to-deal conversion rates and average days to close by source."
  }
];

// ─── Field Reference ──────────────────────────────────────────────────────────

const ZH_FIELD_REFERENCE = [
  {
    scope: "Contact",
    scopeKey: "contact",
    fields: [
      { field: "id", label: "Contact ID", notes: "Zoho internal ID" },
      { field: "Full_Name", label: "Full Name", notes: "First + last name" },
      { field: "Email", label: "Email", notes: "Primary email" },
      { field: "Phone", label: "Phone", notes: "Primary phone number" },
      {
        field: "Account_Name.name",
        label: "Company",
        notes: "Linked account name"
      },
      {
        field: "Lead_Source",
        label: "Lead Source",
        notes: "Web / Referral / Campaign / etc."
      },
      { field: "Owner.name", label: "Owner", notes: "Assigned sales rep" }
    ]
  },
  {
    scope: "Deal",
    scopeKey: "deal",
    fields: [
      { field: "id", label: "Deal ID", notes: "Zoho internal ID" },
      {
        field: "Deal_Name",
        label: "Deal Name",
        notes: "Name of the opportunity"
      },
      { field: "Amount", label: "Amount", notes: "Deal value" },
      { field: "Stage", label: "Stage", notes: "Current pipeline stage" },
      { field: "Closing_Date", label: "Close Date", notes: "YYYY-MM-DD" },
      {
        field: "Probability",
        label: "Win Probability",
        notes: "0-100 percentage"
      },
      { field: "Account_Name.name", label: "Account", notes: "Company name" }
    ]
  },
  {
    scope: "Lead",
    scopeKey: "lead",
    fields: [
      { field: "id", label: "Lead ID", notes: "Zoho internal ID" },
      { field: "Full_Name", label: "Full Name", notes: "Lead name" },
      { field: "Email", label: "Email", notes: "Lead email" },
      { field: "Company", label: "Company", notes: "Lead's company" },
      {
        field: "Lead_Status",
        label: "Status",
        notes: "New / Contacted / Qualified / Lost / etc."
      },
      {
        field: "Lead_Source",
        label: "Source",
        notes: "Web / Referral / Campaign / etc."
      }
    ]
  }
];

// ─── Shared UI ────────────────────────────────────────────────────────────────

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function CopyVariableButton({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(`{${variable}}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ok */
    }
  }
  return (
    <SecondaryButton
      onClick={copy}
      title={`Copy {${variable}}`}
      className={`!text-[10px] !px-1.5 !py-0.5 !border !font-mono ${copied ? "!border-green-300 !bg-green-50 !text-green-700" : "!border-zinc-200 !bg-zinc-50 !text-zinc-500 hover:!border-blue-300 hover:!text-blue-700 hover:!bg-blue-50"}`}
      aria-label={`Copy variable {${variable}}`}
    >
      {copied ? "✓" : `{${variable}}`}
    </SecondaryButton>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 10.5L3 5.5H13L8 10.5Z" />
    </svg>
  );
}

function FindKeyAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-zinc-200 overflow-hidden mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-zinc-50 hover:bg-zinc-100 text-sm font-medium text-zinc-700"
        aria-expanded={open}
      >
        <span>Where to find your Zoho CRM credentials</span>
        <Chevron open={open} />
      </button>
      {open && (
        <>
          <Divider className="my-0" />
          <div className="px-4 pb-4 pt-3 text-sm text-zinc-600 space-y-1.5 bg-white">
            <ol className="list-none space-y-2">
              {[
                "Go to api-console.zoho.com and sign in",
                "Create a Server-based Application → get Client ID and Client Secret",
                "Use the OAuth flow to generate your Access Token and Refresh Token",
                "Select your data center region (matches your Zoho CRM URL — .com, .eu, .in, .au, or .jp)"
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span
                    className="shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: ZH_COLOR }}
                  >
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </>
      )}
    </div>
  );
}

// ─── CapabilitiesPanel ────────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <SectionCard
      title="What's included"
      description={`${CAPABILITIES.length} feature areas · 28 AI tools`}
      action={
        <GhostButton onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? "Hide" : "Show"}
        </GhostButton>
      }
    >
      {open && (
        <>
          <Divider className="my-0 mb-4" />
          <div className="rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>All 28 AI tools activate automatically on connect</strong> —
            no extra configuration required.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-zinc-100 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-zinc-600">
                    <IconByName name={cap.icon} className="w-5 h-5" />
                  </span>
                  <span className="text-sm font-medium text-zinc-700">
                    {cap.title}
                  </span>
                </div>
                <p className="text-xs text-zinc-500">{cap.desc}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ─── HowItWorksCard ───────────────────────────────────────────────────────────

function HowItWorksCard() {
  return (
    <SectionCard
      title="How Zoho CRM works in Velaro"
      description="Connect once — every contact, lead, deal, and account becomes AI-searchable instantly."
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0"
              style={{ backgroundColor: ZH_COLOR }}
            >
              1
            </span>
            <span className="font-semibold text-zinc-700 text-sm">
              Connect Zoho CRM
            </span>
          </div>
          <p className="text-xs text-zinc-500">
            Enter OAuth credentials from api-console.zoho.com and select your
            data center region.
          </p>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path
                d="M3 2L7 5L3 8"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
              2
            </span>
            <span className="font-semibold text-green-800 text-sm">
              28 AI tools activate
            </span>
          </div>
          <p className="text-xs text-green-700">
            Contacts, leads, deals, accounts, activities, and reports — all
            AI-accessible in real time.
          </p>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path
                d="M3 2L7 5L3 8"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs font-bold flex items-center justify-center shrink-0">
              3
            </span>
            <span className="font-semibold text-blue-800 text-sm">
              AI-powered sales support
            </span>
          </div>
          <p className="text-xs text-blue-700">
            Sales reps get full CRM context during every chat — deals, history,
            and next actions at a glance.
          </p>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── OverviewTab ──────────────────────────────────────────────────────────────

function OverviewTab({
  hasConnection,
  onConnect,
  onOpenWizard
}: {
  hasConnection: boolean;
  onConnect(): void;
  onOpenWizard(): void;
}) {
  return (
    <div className="space-y-4">
      <CapabilitiesPanel />
      <HowItWorksCard />
      {!hasConnection ? (
        <SectionCard
          title="Connect your Zoho CRM account"
          description="Enter your OAuth credentials and all your contacts, leads, deals, and accounts become instantly searchable by the AI."
          action={
            <PrimaryButton
              onClick={onConnect}
              className="shrink-0"
              style={{ backgroundColor: ZH_COLOR }}
            >
              Connect Now →
            </PrimaryButton>
          }
        />
      ) : (
        <SectionCard
          title="Launch Starter Workflow Wizard"
          description="Build a Zoho CRM bot in minutes — lead capture, deal lookup, or pipeline reporting."
          action={
            <PrimaryButton
              onClick={onOpenWizard}
              className="shrink-0"
              style={{ backgroundColor: ZH_COLOR }}
            >
              Launch Wizard →
            </PrimaryButton>
          }
        />
      )}
    </div>
  );
}

// ─── AiToolsTab ──────────────────────────────────────────────────────────────

function AiToolsTab({
  hasConnection,
  onOpenWizard
}: {
  hasConnection: boolean;
  onOpenWizard(): void;
}) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <SectionCard
      title="AI Tools"
      description="All tools activate automatically on connect — no configuration required."
      action={
        hasConnection ? (
          <LaunchWizardButton onClick={onOpenWizard} />
        ) : undefined
      }
    >
      <div className="flex items-center gap-2 mb-4">
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
        >
          {hasConnection ? `${AI_TOOLS.length} active` : "No connection"}
        </span>
      </div>
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
              {group}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div
                  key={tool.name}
                  className="rounded-lg border border-zinc-100 bg-zinc-50 p-3"
                >
                  <div className="flex items-start gap-2">
                    <div
                      className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                      style={{ backgroundColor: ZH_COLOR }}
                    />
                    <div>
                      <div className="text-xs font-semibold text-zinc-700">
                        {tool.label}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-400 mb-1">
                        {tool.name}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {tool.description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Divider className="mt-8 mb-8" />
      <SkillControlsPanel integration="zoho" skills={AI_TOOLS} />
    </SectionCard>
  );
}

// ─── AccountsTab ──────────────────────────────────────────────────────────────

interface ZHAddFormState {
  displayName: string;
  clientId: string;
  clientSecret: string;
  accessToken: string;
  refreshToken: string;
  dataCenter: string;
  saving: boolean;
  testing: boolean;
  error: string | null;
  testResult: { success: boolean; message: string } | null;
}

function AccountsTab({
  configs,
  onRefresh
}: {
  configs: ZohoConfig[];
  onRefresh(): void;
}) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<ZHAddFormState>({
    displayName: "",
    clientId: "",
    clientSecret: "",
    accessToken: "",
    refreshToken: "",
    dataCenter: "com",
    saving: false,
    testing: false,
    error: null,
    testResult: null
  });
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);

  async function togglePushTranscripts(config: ZohoConfig) {
    setTogglingId(config.id);
    setPushError(null);
    try {
      const resp = await api.messaging.put(`Zoho/Configs/${config.id}`, {
        pushTranscripts: !config.pushTranscripts
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        setPushError(
          `Failed to update transcript push (HTTP ${resp.status}): ${body || "Unknown error"}`
        );
        return;
      }
      onRefresh();
    } catch (ex) {
      console.error("Zoho togglePushTranscripts failed", ex);
      setPushError(
        `Network error while updating transcript push: ${ex instanceof Error ? ex.message : String(ex)}`
      );
    } finally {
      setTogglingId(null);
    }
  }

  function setField(field: keyof ZHAddFormState, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleDelete(id: number) {
    if (!confirm("Remove this Zoho CRM connection?")) return;
    setDeleting(id);
    try {
      await api.messaging.delete(`Zoho/Configs/${id}`);
      onRefresh();
    } catch (ex) {
      console.error("Zoho delete failed", ex);
    } finally {
      setDeleting(null);
    }
  }

  async function handleSave() {
    if (!form.clientId.trim() || !form.clientSecret.trim()) {
      setForm((f) => ({
        ...f,
        error: "Client ID and Client Secret are required."
      }));
      return;
    }
    setForm((f) => ({ ...f, saving: true, error: null, testResult: null }));
    try {
      const resp = await api.messaging.post("Zoho/Configs", {
        displayName: form.displayName.trim() || "Zoho CRM",
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim(),
        accessToken: form.accessToken.trim(),
        refreshToken: form.refreshToken.trim(),
        dataCenter: form.dataCenter
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setForm((f) => ({
          ...f,
          saving: false,
          error: (d as { error?: string }).error ?? "Failed to save."
        }));
        return;
      }
      setForm({
        displayName: "",
        clientId: "",
        clientSecret: "",
        accessToken: "",
        refreshToken: "",
        dataCenter: "com",
        saving: false,
        testing: false,
        error: null,
        testResult: null
      });
      setShowAdd(false);
      onRefresh();
      ensureDefaultRoutingWorkflow(api.messaging);
    } catch (ex) {
      console.error("Zoho save failed", ex);
      setForm((f) => ({
        ...f,
        saving: false,
        error: "Network error — please try again."
      }));
    }
  }

  async function handleTest() {
    if (!form.clientId.trim()) {
      setForm((f) => ({ ...f, error: "Enter credentials first." }));
      return;
    }
    setForm((f) => ({ ...f, testing: true, testResult: null, error: null }));
    try {
      const resp = await api.messaging.post("Zoho/Test", {
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim(),
        accessToken: form.accessToken.trim(),
        refreshToken: form.refreshToken.trim(),
        dataCenter: form.dataCenter
      });
      const d = (await resp.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string;
        orgName?: string;
      };
      setForm((f) => ({
        ...f,
        testing: false,
        testResult: {
          success: d.success ?? false,
          message: d.success
            ? `Connected${d.orgName ? ` — ${d.orgName}` : ""}`
            : (d.error ?? "Connection test failed.")
        }
      }));
    } catch (ex) {
      console.error("Zoho test failed", ex);
      setForm((f) => ({
        ...f,
        testing: false,
        testResult: { success: false, message: "Network error during test." }
      }));
    }
  }

  return (
    <>
      <SectionCard
        title="Connected Accounts"
        action={
          <PrimaryButton
            onClick={() => setShowAdd(!showAdd)}
            className="!px-3 !py-1.5 !text-xs"
            style={{ backgroundColor: ZH_COLOR }}
          >
            + Add Account
          </PrimaryButton>
        }
      >
        <div>
          {showAdd && (
            <SectionCard className="!rounded-xl mb-4 space-y-3 !bg-zinc-50">
              <div className="text-sm font-semibold text-zinc-700">
                Add Zoho CRM Account
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Display Name <span className="text-zinc-400">(optional)</span>
                </label>
                <FieldInput
                  type="text"
                  value={form.displayName}
                  onChange={(e) => setField("displayName", e.target.value)}
                  placeholder="e.g. My Zoho CRM"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Data Center <span className="text-red-500">*</span>
                </label>
                <FieldInput
                  as="select"
                  value={form.dataCenter}
                  onChange={(e) => setField("dataCenter", e.target.value)}
                >
                  {DATA_CENTERS.map((dc) => (
                    <option key={dc.value} value={dc.value}>
                      {dc.label}
                    </option>
                  ))}
                </FieldInput>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">
                    Client ID <span className="text-red-500">*</span>
                  </label>
                  <FieldInput
                    type="text"
                    value={form.clientId}
                    onChange={(e) => setField("clientId", e.target.value)}
                    placeholder="From api-console.zoho.com"
                    className="font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">
                    Client Secret <span className="text-red-500">*</span>
                  </label>
                  <FieldInput
                    type="password"
                    value={form.clientSecret}
                    onChange={(e) => setField("clientSecret", e.target.value)}
                    placeholder="Client Secret"
                    autoComplete="new-password"
                    className="font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">
                    Access Token
                  </label>
                  <FieldInput
                    type="password"
                    value={form.accessToken}
                    onChange={(e) => setField("accessToken", e.target.value)}
                    placeholder="OAuth access token"
                    autoComplete="new-password"
                    className="font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">
                    Refresh Token
                  </label>
                  <FieldInput
                    type="password"
                    value={form.refreshToken}
                    onChange={(e) => setField("refreshToken", e.target.value)}
                    placeholder="OAuth refresh token"
                    autoComplete="new-password"
                    className="font-mono"
                  />
                </div>
              </div>
              <FindKeyAccordion />
              {form.error && (
                <div
                  className="rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-800"
                  role="alert"
                >
                  {form.error}
                </div>
              )}
              {form.testResult && (
                <div
                  className={`rounded-lg p-3 text-sm ${form.testResult.success ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`}
                  role="status"
                >
                  {form.testResult.message}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <SecondaryButton
                  onClick={handleTest}
                  disabled={form.testing || form.saving}
                  className="!text-xs !px-3 !py-1.5"
                >
                  {form.testing ? "Testing…" : "Test Connection"}
                </SecondaryButton>
                <PrimaryButton
                  onClick={handleSave}
                  disabled={form.saving || form.testing}
                  className="!px-4 !py-1.5 !text-xs"
                  style={{ backgroundColor: ZH_COLOR }}
                >
                  {form.saving ? "Saving…" : "Save"}
                </PrimaryButton>
                <GhostButton
                  onClick={() => {
                    setShowAdd(false);
                    setForm({
                      displayName: "",
                      clientId: "",
                      clientSecret: "",
                      accessToken: "",
                      refreshToken: "",
                      dataCenter: "com",
                      saving: false,
                      testing: false,
                      error: null,
                      testResult: null
                    });
                  }}
                  className="!text-xs !px-3 !py-1.5 !text-zinc-600 hover:!text-zinc-800 hover:!no-underline"
                >
                  Cancel
                </GhostButton>
              </div>
            </SectionCard>
          )}

          {configs.length === 0 && !showAdd ? (
            <div className="text-center py-12 text-zinc-400">
              <div className="flex justify-center mb-3 text-zinc-400">
                <IconByName name="BarChart2" className="w-10 h-10" />
              </div>
              <div className="font-medium text-zinc-600 mb-1">
                No Zoho CRM account connected
              </div>
              <p className="text-sm">
                Connect your Zoho credentials to enable AI-powered sales support
                in every conversation.
              </p>
              <PrimaryButton
                onClick={() => setShowAdd(true)}
                className="mt-4"
                style={{ backgroundColor: ZH_COLOR }}
              >
                Connect Zoho CRM →
              </PrimaryButton>
            </div>
          ) : (
            <div className="space-y-3">
              {configs.map((c) => (
                <div
                  key={c.id}
                  className="rounded-xl border border-zinc-200 p-4 flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold"
                      style={{ backgroundColor: ZH_COLOR }}
                    >
                      Z
                    </div>
                    <div>
                      <div className="font-medium text-zinc-800">
                        {c.displayName}
                      </div>
                      <div className="text-xs text-zinc-400 font-mono">
                        Client ID: {c.clientIdMasked} · Region: .{c.dataCenter}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                    >
                      {c.enabled ? "Active" : "Disabled"}
                    </span>
                    <GhostButton
                      onClick={() => handleDelete(c.id)}
                      disabled={deleting === c.id}
                      className="!text-xs !text-red-500 hover:!text-red-700 !no-underline hover:!no-underline"
                    >
                      {deleting === c.id ? "Removing..." : "Remove"}
                    </GhostButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      {configs.length > 0 && (
        <SectionCard title="Transcript Auto-Push" className="mt-4">
          <div className="text-sm text-zinc-500 mb-4">
            When enabled, the full chat transcript is automatically attached as
            a note on the matched Zoho CRM contact after every conversation
            ends.
          </div>
          {pushError && (
            <div
              className="rounded-lg p-3 mb-3 text-sm bg-red-50 border border-red-200 text-red-800"
              role="alert"
            >
              {pushError}
            </div>
          )}
          <div className="space-y-3">
            {configs.map((config) => (
              <div
                key={config.id}
                className="flex items-center justify-between gap-4 p-3 border-b border-zinc-100 last:border-b-0"
              >
                <div>
                  <div className="text-sm font-medium text-zinc-700">
                    {config.displayName}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {config.pushTranscripts
                      ? "Transcript attached to contact after each conversation"
                      : "Transcript push is off for this connection"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => togglePushTranscripts(config)}
                  disabled={togglingId === config.id}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none shrink-0 disabled:opacity-50 ${config.pushTranscripts ? "bg-green-500" : "bg-zinc-300"}`}
                  aria-label={
                    config.pushTranscripts
                      ? "Disable transcript push"
                      : "Enable transcript push"
                  }
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.pushTranscripts ? "translate-x-4" : "translate-x-0.5"}`}
                  />
                </button>
              </div>
            ))}
          </div>
          {configs.some((c) => c.pushTranscripts && c.enabled) && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
              Auto-push active — transcripts will appear as notes on the matched
              Zoho CRM contact after each conversation.
            </div>
          )}
        </SectionCard>
      )}
    </>
  );
}

// ─── FieldReferenceTab ────────────────────────────────────────────────────────

function FieldReferenceTab() {
  const [activeScope, setActiveScope] = useState("Contact");
  const scopeData =
    ZH_FIELD_REFERENCE.find((s) => s.scope === activeScope) ??
    ZH_FIELD_REFERENCE[0];
  return (
    <SectionCard
      title="Field Reference"
      description="Variable names for HTTP POST nodes to push Zoho CRM data to your systems."
    >
      <div>
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-4 text-xs text-blue-800">
          <div className="font-semibold mb-2">Example HTTP POST variables</div>
          <CodeBlock>{`{
  "contact_name":   "{zoho.contact.Full_Name}",
  "contact_email":  "{zoho.contact.Email}",
  "deal_name":      "{zoho.deal.Deal_Name}",
  "deal_amount":    "{zoho.deal.Amount}",
  "deal_stage":     "{zoho.deal.Stage}",
  "lead_source":    "{zoho.lead.Lead_Source}"
}`}</CodeBlock>
        </div>
        <div className="flex gap-2 mb-4 flex-wrap">
          {ZH_FIELD_REFERENCE.map((s) => (
            <button
              key={s.scope}
              type="button"
              onClick={() => setActiveScope(s.scope)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeScope === s.scope ? "text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
              style={
                activeScope === s.scope ? { backgroundColor: ZH_COLOR } : {}
              }
            >
              {s.scope}
            </button>
          ))}
        </div>
        <div className="rounded-xl border border-zinc-200 overflow-hidden">
          <div
            className="px-4 py-2.5 text-white text-xs font-semibold"
            style={{ backgroundColor: ZH_COLOR }}
          >
            {scopeData.scope} fields
          </div>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                    Field
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                    Label
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                    Variable
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {scopeData.fields.map((f, i) => (
                  <tr
                    key={i}
                    className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}
                  >
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">
                      {f.field}
                    </td>
                    <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                    <td className="px-3 py-2">
                      <CopyVariableButton
                        variable={`zoho.${scopeData.scopeKey}.${f.field}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-zinc-400 italic">
                      {f.notes}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Zoho() {
  const api = useApi();
  const [configs, setConfigs] = useState<ZohoConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const { teamId } = useSelectedTeam();

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.messaging.get("Zoho/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch (ex) {
      console.error("Zoho fetchConfigs failed", ex);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    if (configs.length > 0 && selectedConfigId === 0) {
      setSelectedConfigId(configs.find((c) => c.enabled)?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  const hasActiveConfig = configs.some((c) => c.enabled);

  const tabsJSX = (
    <>
      <IntegrationTab
        active={activeTab === "overview"}
        onClick={() => setActiveTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "accounts"}
        onClick={() => setActiveTab("accounts")}
      >
        Accounts
        {configs.length > 0 && (
          <span className="text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">
            {configs.length}
          </span>
        )}
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "ai-tools"}
        onClick={() => setActiveTab("ai-tools")}
      >
        AI Skills
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "field-reference"}
        onClick={() => setActiveTab("field-reference")}
      >
        Field Reference
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "data-mapping"}
        onClick={() => setActiveTab("data-mapping")}
      >
        Field Mapping
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "logs"}
        onClick={() => setActiveTab("logs")}
      >
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Zoho CRM" tabs={tabsJSX}>
      {showWizard && (
        <StarterWorkflowWizard
          integration="zoho"
          onClose={() => setShowWizard(false)}
        />
      )}

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-blue-800 mb-0.5">
              Deploy the chat widget to your site
            </div>
            <div className="text-xs text-blue-600">
              Your Zoho CRM integration is connected — grab your embed code from
              Deployments to go live.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/chat_window"
              className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 transition"
            >
              Chat Window
            </Link>
            <Link
              to="/deployment"
              className="text-xs px-3 py-1.5 rounded-lg bg-(--ds-primary) hover:opacity-90 text-white transition"
            >
              Get Embed Code →
            </Link>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab
              hasConnection={hasActiveConfig}
              onConnect={() => setActiveTab("accounts")}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}
          {activeTab === "accounts" && (
            <AccountsTab configs={configs} onRefresh={fetchConfigs} />
          )}
          {activeTab === "ai-tools" && (
            <AiToolsTab
              hasConnection={hasActiveConfig}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}
          {activeTab === "field-reference" && <FieldReferenceTab />}
          {activeTab === "data-mapping" && (
            <SectionCard title="Field Mapping — Zoho CRM">
              <ZohoDataMapping
                teamId={teamId ?? 0}
                selectedConfigId={selectedConfigId}
              />
            </SectionCard>
          )}
          {activeTab === "logs" && (
            <SectionCard title="Activity Log">
              <IntegrationActivityLog integrationSlug="Zoho" />
            </SectionCard>
          )}
        </>
      )}
    </StickyRouteHeader>
  );
}
